/**
 * One homework page as the picture the frames are drawn over (#377, #661; `docs/feedback_research.md` principle 5).
 *
 * - **Drawn in the page's own pixels.** An SVG whose `viewBox` is the page image's `widthPx x heightPx`, the coordinate space every reader
 *   box and note frame is given in (`note-location@1`), so the picture is placed by the server's numbers as they are. The frames over it are
 *   `feedback-frames.tsx`, positioned as shares of the same box.
 * - **Lines framed with no verdict at all** (`lineFrames`, the "how sure were you" step) are neutral frames, with no mark: where a question's
 *   lines are. `stripLabel` names the question above the first of them.
 */

import { API_BASE_URL } from '../../api/client.js';
import { useFeedbackLabels } from '../../i18n/homework-feedback-labels.js';
import { useMessages } from '../../i18n/locale.js';
import type { Bbox } from './feedback-blocks.js';
import { feedbackChrome } from './feedback-chrome.js';
import { frameOutline } from './feedback-frames.js';

export type { Bbox } from './feedback-blocks.js';

export interface FeedbackPageImageProps {
  readonly pageId: string;
  readonly pageNumber: number;
  readonly widthPx: number;
  readonly heightPx: number;
  /** Lines framed with no verdict at all, in neutral: where a question's lines are. */
  readonly lineFrames?: readonly Bbox[];
  /** The question's name, drawn as a strip above the first framed line. */
  readonly stripLabel?: string | null;
}

/** The page image as served to its own learner (`GET /homework/pages/{pageId}/image`). */
export function pageImageUrl(pageId: string): string {
  return `${API_BASE_URL}/homework/pages/${encodeURIComponent(pageId)}/image`;
}

export function FeedbackPageImage({ pageId, pageNumber, widthPx, heightPx, lineFrames = [], stripLabel = null }: FeedbackPageImageProps): React.JSX.Element {
  const t = useFeedbackLabels();
  const m = useMessages(feedbackChrome);
  const first = lineFrames[0];
  return (
    <div className="fb-page-image">
      <svg className="fb-page-svg" viewBox={`0 0 ${String(widthPx)} ${String(heightPx)}`} role="img" aria-label={t.pageImageLabel(pageNumber)}>
        <image href={pageImageUrl(pageId)} x={0} y={0} width={widthPx} height={heightPx} />
      </svg>
      {lineFrames.length > 0 ? (
        <div className="fb-frames">
          {lineFrames.map((box, index) => (
            <div
              key={`line-${String(index)}`}
              className="fb-frame feedback-frame-line"
              data-kind="line"
              data-outline={frameOutline('line')}
              style={{
                left: `${String((box.x0 / widthPx) * 100)}%`,
                top: `${String((box.y0 / heightPx) * 100)}%`,
                width: `${String(((box.x1 - box.x0) / widthPx) * 100)}%`,
                height: `${String(((box.y1 - box.y0) / heightPx) * 100)}%`,
              }}
            >
              {index === 0 && first !== undefined && stripLabel !== null ? (
                <p className="fb-strip">
                  <span className="fb-strip-label">{m.questionStrip(stripLabel)}</span>
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
